export interface PermissionCheck {
  permission: Permissions;
  level?: PermissionLevel;
}

export enum PermissionLevel {
  INDIVIDUAL = 'Individual',
  BROKERAGE = 'Brokerage',
  OFFICE = 'Office',
}

export interface Permission {
  PartyPermKey: number;
  PartyPermAccessLevelType: string;
  PartyPermPermissionType: string;
  PartyPermGrantorPartyKey: number;
  PartyPermGranteePartyKey: number;
  PartyPermSystemLocale: string;
  PpPermissionGroup: string;
  PartyPermSubSystemLocale: string;
  PartyPermModificationTimestamp: string;
}

export function toPermissionKey(permission: Permissions) {
  return PermissionMapping[Number(permission)];
}

export enum Permissions {
  ACCESS_OWN_OFFICE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 200004305320,
  ACCESS_SPECIFIC_OFFICES_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 200005032312,
  ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 200005032313,
  ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 200005032314,
  ADD_OR_EDIT_BROKER_OPEN_HOUSE = 200005032316,
  ADD_OR_EDIT_PUBLIC_OPEN_HOUSE = 200005032317,
  DECREASE_LISTING_EXPIRATION_DATE = 200005032319,
  EDIT_LISTING_EXPIRATION_WITHDRAWN_DATE = 200005032320,
  EDIT_REMARKS_AGENT = 200005032321,
  EDIT_REMARKS_BROKERAGE = 200005032322,
  EDIT_REMARKS_CONFIRMATION_AND_ENTRY_INFORMATION = 200005032323,
  EDIT_REMARKS_CUSTOMER_PUBLIC_GREEN = 200005032324,
  EDIT_REMARKS_OFFICE = 200005032325,
  EDIT_REMARKS_SHOWING_INSTRUCTIONS = 200005032326,
  EDIT_SHOWING_OFFICE_SETUP = 200005032327,
  ADD_EDIT_VIRTUAL_TOURS = 200005032328,
  EXTEND_LISTING_EXPIRATION_DATE = 200005032329,
  MANAGE_BRIGHT_MLS_NEWS = 200005032330,
  MONITOR_OFFICE_ASSOCIATION_NEWS = 200005032331,
  UPLOAD_DOCUMENTS = 200005032332,
  UPLOAD_LISTING_PHOTOS = 200005032333,
  VIEW_EXTENDED_ERROR_INFORMATION = 200005032334,
  CANCEL_LISTINGS = 200005032335,
  ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 200005032336,
  PUBLISH_CHANGES_TO_EXISTING_LISTINGS = 200005093870,
  CAN_EDIT_LISTINGS = 200005144858,
  CAN_IMPERSONATE = 200005144859,
  CAN_WORK_ON_BEHALF_OF = 200005144860,
  DELETE_LISTING = 200005593351,
  DATA_CORRECTIONS = 200005646727,
  ADD_EDIT_ALL_OTHER_LISTING_DATA = 200007143642,
  ACCESS_LISTING_MANAGEMENT = 205001868197,
  ADD_NON_MEMBER_LISTINGS = 205001868198,
  ADD_OR_EDIT_UNPUBLISHED_LISTINGS = 205001868199,
  SELF_SELECT_WHO_CAN_EDIT_HIS_HER_LISTINGS = 205001868207,
  EDIT_COOPERATIVE_COMPENSATION = 205001868208,
  EDIT_CURRENT_PRICE = 205001868209,
  EDIT_LISTING_CONTRACT_DATE = 205001868210,
  EDIT_LISTING_STATUS = 205001868211,
  EDIT_OTHER_MEMBERS_PERMISSIONS = 205001868212,
  EDIT_BRIGHT_MLS_EMPLOYEE_PERMISSION = 205001868214,
  LISTING_PERFORMANCE_REPORT = 205001868215,
  MANAGE_ASSOCIATION_NEWS = 205001868216,
  MANAGE_OFFICE_NEWS = 205001868217,
  PUBLISH_NEW_LISTINGS = 205001868220,
  APPRAISER = 205001868221,
  ASST_STAFF_ACCESS_TO_ALL_BROKERAGE_LISTINGS = 205001868222,
  ASST_STAFF_ACCESS_TO_SPECIFIC_OFFICES_LISTINGS = 205001868223,
  ASST_STAFF_ACCESS_TO_OWN_OFFICE_LISTINGS = 205001868224,
  ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_ONLY = 205001868225,
  BETA_TESTERS = 205001868226,
  ACCESS_ALL_BROKERAGE_LISTINGS = 205001868227,
  ACCESS_ALL_BROKERAGE_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 205001868228,
  CORPORATE_OR_ASSOCIATION_EXECUTIVE = 205001868229,
  GOVERNMENT_AGENCY = 205001868230,
  SELF_SELECT_WHO_CAN_MANAGE_HIS_HER_CLIENTS = 205001868231,
  BRIGHT_MLS_EMPLOYEE_ADMIN = 205001868232,
  BRIGHT_MLS_EMPLOYEE = 205001868233,
  ACCESS_SPECIFIC_OFFICES_LISTINGS = 205001868234,
  NO_ACCESS = 205001868235,
  ACCESS_OWN_OFFICE_LISTINGS = 205001868236,
  ACCESS_PERSONAL_LISTINGS_ONLY = 205001868237,
  VIEW_ONLY = 205001868238,
  LIMITED_ACCESS = 550015174558,
  TEAM_LEAD = 550016232710,
  TEAM_EDIT = 550016232854,
  PRODUCTIVITY_REPORTS = 550016232856,
  ACCESS_PERSONAL_LISTINGS_AND_RANKING_AND_PRODUCTION_REPORTS = 550017228704,
  ASST_STAFF_ACCESS_TO_PERSONAL_LISTINGS_RANKING_AND_PRODUCTION_REPORTS = 550017228706,
  SYNDICATE_LISTINGS_AS_TEAM_LEAD = 550017521168,
  OFFER_MANAGEMENT = 550035013490,
  ALLOW_SHOWINGS_TO_BE_MANAGED_IN_BRIGHT_MLS = 550035904720,
  SHOWING_TIME_ONLY = 550035904724,
  SHOWING_HUB_PROVIDERS_ONLY = 550035904728,
  ALL_SHOWING_PROVIDERS = 550035904732,
  ALL = 550036172244,
  SENTRI_KEY = 550036172246,
  DUMMY_OPTION = 550036172248,
  EARNEST_MONEY = 550036227754,
  EDIT_CONCESSION_IN_PRICE = 550036229960,
}

export const PermissionMapping: { [key: number]: string } = {
  200004305320: 'Access Own Office Listings and Ranking and Production Reports',
  200005032312:
    'Access Specific Offices Listings and Ranking and Production Reports',
  200005032313:
    'Asst/Staff access to all brokerage listings, Ranking and Production Reports',
  200005032314:
    'Asst/Staff access to specific offices listings, Ranking and Production Reports',
  200005032316: 'Add or Edit Broker Open House',
  200005032317: 'Add or Edit Public Open House',
  200005032319: 'Decrease Listing Expiration Date',
  200005032320: 'Edit Listing Expiration/Withdrawn Date',
  200005032321: 'Edit Remarks - Agent',
  200005032322: 'Edit Remarks - Brokerage',
  200005032323: 'Edit Remarks - Confirmation and Entry Information',
  200005032324: 'Edit Remarks - Customer/Public/Green',
  200005032325: 'Edit Remarks - Office',
  200005032326: 'Edit Remarks - Showing Instructions',
  200005032327: 'Edit Showing Office Setup',
  200005032328: 'Add/Edit Virtual Tours',
  200005032329: 'Extend Listing Expiration Date',
  200005032330: 'Manage Bright MLS News',
  200005032331: 'Monitor Office/Association News',
  200005032332: 'Upload Documents',
  200005032333: 'Upload Listing Photos',
  200005032334: 'View Extended Error Information',
  200005032335: 'Cancel Listings',
  200005032336:
    'Asst/Staff access to own office listings, Ranking and Production Reports',
  200005093870: 'Publish Changes to Existing Listings',
  200005144858: 'Can Edit Listings',
  200005144859: 'Can Impersonate',
  200005144860: 'Can Work on Behalf Of',
  200005593351: 'Delete Listing',
  200005646727: 'Data Corrections',
  200007143642: 'Add/Edit All Other Listing Data',
  205001868197: 'Access Listing Management',
  205001868198: 'Add Non-Member Listings',
  205001868199: 'Add or Edit Unpublished Listings',
  205001868207: 'Self-select who can edit his/her listings',
  205001868208: 'Edit Cooperative Compensation',
  205001868209: 'Edit Current Price',
  205001868210: 'Edit Listing Contract Date',
  205001868211: 'Edit Listing Status',
  205001868212: "Edit Other Members' Permissions",
  205001868214: 'Edit Bright MLS Employee Permission',
  205001868215: 'Listing Performance Report',
  205001868216: 'Manage Association News',
  205001868217: 'Manage Office News',
  205001868220: 'Publish New Listings',
  205001868221: 'Appraiser',
  205001868222: 'Asst/Staff access to all brokerage listings',
  205001868223: 'Asst/Staff access to specific offices listings',
  205001868224: 'Asst/Staff access to own office listings',
  205001868225: 'Asst/Staff access to personal listings only',
  205001868226: 'Beta Testers',
  205001868227: 'Access All Brokerage Listings',
  205001868228:
    'Access All Brokerage Listings and Ranking and Production Reports',
  205001868229: 'Corporate or Association Executive',
  205001868230: 'Government Agency',
  205001868231: 'Self-select who can manage his/her clients',
  205001868232: 'Bright MLS Employee - Admin',
  205001868233: 'Bright MLS Employee',
  205001868234: 'Access Specific Offices Listings ',
  205001868235: 'No Access',
  205001868236: 'Access Own Office Listings',
  205001868237: 'Access Personal Listings Only',
  205001868238: 'View Only',
  550015174558: 'Limited Access',
  550016232710: 'Team Lead',
  550016232854: 'Team Edit',
  550016232856: 'Productivity Reports',
  550017228704: 'Access Personal Listings and Ranking and Production Reports',
  550017228706:
    'Asst/Staff access to personal listings, Ranking and Production Reports',
  550017521168: 'Syndicate Listings As Team Lead',
  550035013490: 'Offer Management',
  550035904720: 'Allow Showings to be managed in Bright',
  550035904724: 'ShowingTime Only',
  550035904728: 'Showing Hub Providers Only',
  550035904732: 'All Showing Providers',
  550036172244: 'All',
  550036172246: 'SentriKey',
  550036172248: 'Dummy Option',
  550036227754: 'Earnest Money',
  550036229960: 'Edit Concession in Price',
};
